// Pantalla del profesor para gestionar su disponibilidad recurrente + excepciones puntuales.

import { useEffect, useState } from 'react';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { Spinner } from '@/components/atoms/Spinner';
import { WeeklyView } from '@/components/molecules/WeeklyView';
import { Header } from '@/components/organisms/Header';
import { ApiError } from '@/services/api';
import {
  createException,
  createPattern,
  deletePattern,
  listMyPatterns,
  type AvailabilityPattern,
} from '@/services/availability';
import styles from './Disponibilidad.module.scss';

const WEEKDAYS_LONG = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

export function Disponibilidad() {
  const [patterns, setPatterns] = useState<AvailabilityPattern[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form patrón
  const [pWeekday, setPWeekday] = useState(0);
  const [pStart, setPStart] = useState('09:00');
  const [pEnd, setPEnd] = useState('13:00');
  const [pSubmitting, setPSubmitting] = useState(false);

  // Form excepción
  const [eDate, setEDate] = useState('');
  const [eMode, setEMode] = useState<'block' | 'extra'>('block');
  const [eAllDay, setEAllDay] = useState(true);
  const [eStart, setEStart] = useState('09:00');
  const [eEnd, setEEnd] = useState('13:00');
  const [eReason, setEReason] = useState('');
  const [eSubmitting, setESubmitting] = useState(false);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const list = await listMyPatterns();
      setPatterns(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, []);

  async function addPattern() {
    if (pStart >= pEnd) { setError('Hora inicio debe ser anterior a hora fin'); return; }
    setPSubmitting(true);
    setError(null);
    try {
      await createPattern({ weekday: pWeekday, start_time: `${pStart}:00`, end_time: `${pEnd}:00` });
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error al crear');
    } finally {
      setPSubmitting(false);
    }
  }

  async function removePattern(id: number) {
    setError(null);
    try {
      await deletePattern(id);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al borrar');
    }
  }

  async function addException() {
    if (!eDate) { setError('Indica fecha'); return; }
    setESubmitting(true);
    setError(null);
    try {
      await createException({
        exception_date: eDate,
        is_available: eMode === 'extra',
        ...(eAllDay ? {} : { start_time: `${eStart}:00`, end_time: `${eEnd}:00` }),
        ...(eReason.trim() ? { reason: eReason.trim() } : {}),
      });
      setEDate(''); setEReason('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error al crear excepción');
    } finally {
      setESubmitting(false);
    }
  }

  return (
    <>
      <Header title="Mi disponibilidad" />
      <main className={styles.main}>
        {loading ? (
          <div className={styles.center}><Spinner size="lg" /></div>
        ) : (
          <>
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Vista semanal</h2>
              <p className={styles.muted}>Bloques pintados = horarios recurrentes en los que estás disponible.</p>
              <WeeklyView patterns={patterns} />
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Patrones recurrentes</h2>
              <p className={styles.muted}>Añade un día y rango horario que se repite cada semana.</p>

              <div className={styles.formRow}>
                <label className={styles.field}>
                  <span>Día</span>
                  <select value={pWeekday} onChange={(e) => setPWeekday(Number(e.target.value))}>
                    {WEEKDAYS_LONG.map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </select>
                </label>
                <label className={styles.field}>
                  <span>Inicio</span>
                  <input type="time" value={pStart} onChange={(e) => setPStart(e.target.value)} />
                </label>
                <label className={styles.field}>
                  <span>Fin</span>
                  <input type="time" value={pEnd} onChange={(e) => setPEnd(e.target.value)} />
                </label>
                <Button variant="primary" disabled={pSubmitting} onClick={() => void addPattern()}>
                  <Icon name="plus" size={14} /> Añadir
                </Button>
              </div>

              {patterns.length === 0 ? (
                <p className={styles.muted}>Aún no tienes patrones. Añade el primero arriba.</p>
              ) : (
                <ul className={styles.list}>
                  {patterns.map((p) => (
                    <li key={p.id} className={styles.row}>
                      <Badge tone="accent">{WEEKDAYS_LONG[p.weekday]}</Badge>
                      <span>{p.start_time.slice(0, 5)} – {p.end_time.slice(0, 5)}</span>
                      <Button variant="ghost" size="sm" onClick={() => void removePattern(p.id)}>
                        <Icon name="trash" size={14} /> Borrar
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Excepciones puntuales</h2>
              <p className={styles.muted}>Bloquea un día (vacaciones) o añade un hueco extra fuera de tus patrones.</p>

              <div className={styles.formRow}>
                <label className={styles.field}>
                  <span>Tipo</span>
                  <select value={eMode} onChange={(e) => setEMode(e.target.value as 'block' | 'extra')}>
                    <option value="block">Bloquear (no disponible)</option>
                    <option value="extra">Añadir hueco extra</option>
                  </select>
                </label>
                <label className={styles.field}>
                  <span>Fecha</span>
                  <input type="date" value={eDate} onChange={(e) => setEDate(e.target.value)} />
                </label>
                <label className={`${styles.field} ${styles.checkbox}`}>
                  <input type="checkbox" checked={eAllDay} onChange={(e) => setEAllDay(e.target.checked)} /> Todo el día
                </label>
              </div>

              {!eAllDay && (
                <div className={styles.formRow}>
                  <label className={styles.field}>
                    <span>Inicio</span>
                    <input type="time" value={eStart} onChange={(e) => setEStart(e.target.value)} />
                  </label>
                  <label className={styles.field}>
                    <span>Fin</span>
                    <input type="time" value={eEnd} onChange={(e) => setEEnd(e.target.value)} />
                  </label>
                </div>
              )}

              <div className={styles.formRow}>
                <label className={styles.field}>
                  <span>Motivo (opcional)</span>
                  <input type="text" value={eReason} maxLength={200} onChange={(e) => setEReason(e.target.value)} placeholder="Vacaciones, formación..." />
                </label>
                <Button variant="primary" disabled={eSubmitting || !eDate} onClick={() => void addException()}>
                  <Icon name="plus" size={14} /> Añadir excepción
                </Button>
              </div>
              <p className={styles.helper}>
                Las excepciones se aplican automáticamente al cálculo de huecos libres del alumno. Para gestionarlas/borrarlas, contacta con el equipo (UI completa en próxima fase).
              </p>
            </section>

            {error && <p className={styles.error}>{error}</p>}
          </>
        )}
      </main>
    </>
  );
}
